import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "gastronomy-page container py-4" };
const _hoisted_2 = {
    key: 0,
    class: "loading-state"
};
const _hoisted_3 = {
    key: 1,
    class: "error-state"
};
const _hoisted_4 = { key: 2 };
const _hoisted_5 = {
    key: 3,
    class: "empty-state"
};
import { ref, onMounted, watch, computed, getCurrentInstance } from 'vue';
import { useRoute } from 'vue-router';
import GastronomyHero from '@/components/gastronomy/GastronomyHero.vue';
import GastronomyHeader from '@/components/gastronomy/GastronomyHeader.vue';
import GastronomyDetails from '@/components/gastronomy/GastronomyDetails.vue';
import GastronomyNearbyCarousel from '@/components/gastronomy/GastronomyNearbyCarousel.vue';
import GastronomyAmenitiesSection from '@/components/gastronomy/GastronomyAmenitiesSection.vue';
import GastronomyMenuSection from '@/components/gastronomy/GastronomyMenuSection.vue';
import GastronomyPoliciesSection from '@/components/gastronomy/GastronomyPoliciesSection.vue';
import GastronomyHowToArriveSection from '@/components/gastronomy/GastronomyHowToArriveSection.vue';
import GastronomyStickyCTA from '@/components/gastronomy/GastronomyStickyCTA.vue';
import { gastronomyService } from '@/services/gastronomyService';
import { getLocationById, mapLocationToGastronomyLike } from '@/services/locations.service';
import favoritesService from '@/services/favorites.service';
import localFavoritesService from '@/services/localFavorites.service';
import { useAuthStore } from '@/store/auth';
// Props para recibir el ID del restaurante desde la ruta
export default /*@__PURE__*/ _defineComponent({
    __name: 'GastronomyPage',
    props: {
        id: {}
    },
    setup(__props) {
        const props = __props;
        const route = useRoute();
        const app = getCurrentInstance();
        const currentRestaurant = ref(null);
        const loading = ref(false);
        const error = ref(null);
        const userLatitude = ref(undefined);
        const userLongitude = ref(undefined);
        const authStore = useAuthStore();
        const isAuthenticated = computed(() => authStore.authenticated);
        const favoriteLoading = ref(false);
        const isFavorite = ref(false);
        const favoriteId = ref(null);
        const favouriteButtonText = computed(() => isFavorite.value ? 'EN FAVORITOS' : 'AGREGAR A FAVORITOS');
        const hasLocationPolicies = ref(true); // Por defecto true para mostrar durante carga inicial
        // Función para obtener el ID del lugar (desde props o route params)
        const getRestaurantId = () => {
            return props.id || route.params.id;
        };
        // Función para obtener la imagen principal
        const getMainImage = (restaurant) => {
            const buildImg = app?.appContext?.config?.globalProperties?.$buildImg;
            const mainImage = restaurant.images?.find((img) => img.is_main);
            if (mainImage) {
                return buildImg ? buildImg(mainImage.image_url) : mainImage.image_url;
            }
            if (restaurant.images && restaurant.images.length > 0) {
                const url = restaurant.images[0].image_url;
                return buildImg ? buildImg(url) : url;
            }
            return 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop';
        };
        // Función para convertir tipo de item a etiqueta legible
        const getItemTypeLabel = (type) => {
            const labels = {
                'room': 'Habitación',
                'service': 'Servicio',
                'offer': 'Oferta',
                'food': 'Comida',
                'drink': 'Bebida'
            };
            return labels[type] || 'Servicio';
        };
        const buildFavoriteCardFromRestaurant = (restaurant) => ({
            id: restaurant.id,
            type: 'gastronomy',
            title: restaurant.name,
            description: restaurant.description || '',
            image: getMainImage(restaurant),
            location: restaurant.location?.department?.name || restaurant.location?.name || restaurant.city || '',
            category: restaurant.cuisine_type || '',
            badge: restaurant.badge || restaurant.cuisine_type || '',
            link: `/location/${restaurant.id}`,
            locationId: restaurant.id,
            favoritesCount: restaurant.favorites_count || restaurant.favoritesCount || 0
        });
        const refreshFavoriteState = async () => {
            if (!currentRestaurant.value) {
                isFavorite.value = false;
                favoriteId.value = null;
                return;
            }
            const restaurantId = currentRestaurant.value.id;
            if (isAuthenticated.value) {
                try {
                    const response = await favoritesService.getUserFavorites();
                    if (response.success && response.data) {
                        const match = response.data.find(fav => String(fav.location_id) === String(restaurantId));
                        if (match) {
                            isFavorite.value = true;
                            favoriteId.value = match.id;
                            return;
                        }
                    }
                    isFavorite.value = false;
                    favoriteId.value = null;
                }
                catch (error) {
                    console.warn('No se pudieron obtener favoritos del servidor, usando locales como fallback.');
                    const localMatch = localFavoritesService.findByLocationId(restaurantId);
                    isFavorite.value = !!localMatch;
                    favoriteId.value = localMatch?.id ?? null;
                }
            }
            else {
                const localMatch = localFavoritesService.findByLocationId(restaurantId);
                isFavorite.value = !!localMatch;
                favoriteId.value = localMatch?.id ?? null;
            }
        };
        const handleFavoriteToggle = async () => {
            if (!currentRestaurant.value || favoriteLoading.value)
                return;
            favoriteLoading.value = true;
            const restaurant = currentRestaurant.value;
            try {
                if (isFavorite.value) {
                    if (isAuthenticated.value) {
                        await favoritesService.removeFavorite(restaurant.id);
                    }
                    else if (favoriteId.value) {
                        localFavoritesService.remove(favoriteId.value);
                    }
                    isFavorite.value = false;
                    favoriteId.value = null;
                }
                else {
                    if (isAuthenticated.value) {
                        const response = await favoritesService.addFavorite(restaurant.id);
                        if (response.success && response.data) {
                            isFavorite.value = true;
                            favoriteId.value = response.data.id;
                        }
                    }
                    else {
                        const updated = localFavoritesService.add(buildFavoriteCardFromRestaurant(restaurant));
                        const stored = updated.find(item => String(item.locationId) === String(restaurant.id));
                        isFavorite.value = true;
                        favoriteId.value = stored?.id ?? null;
                    }
                }
            }
            catch (error) {
                console.error('Error al actualizar favorito:', error);
            }
            finally {
                favoriteLoading.value = false;
            }
        };
        // Función para cargar el lugar específico (restaurante, museo, ciudad, etc.)
        const fetchRestaurant = async () => {
            const locationId = getRestaurantId();
            if (!locationId) {
                error.value = 'ID de lugar no proporcionado';
                return;
            }
            loading.value = true;
            error.value = null;
            try {
                // Primero intentar con el servicio de gastronomía (para restaurantes)
                try {
                    const response = await gastronomyService.getRestaurantById(locationId);
                    if (response.success && response.data) {
                        currentRestaurant.value = response.data;
                        return;
                    }
                }
                catch (gastroError) {
                    console.log('No es un restaurante, intentando con servicio de ubicaciones...');
                }
                // Si no es un restaurante, usar el servicio de ubicaciones genérico
                const location = await getLocationById(locationId);
                if (location) {
                    currentRestaurant.value = mapLocationToGastronomyLike(location);
                }
                else {
                    throw new Error('Lugar no encontrado');
                }
            }
            catch (err) {
                console.error('Error fetching location:', err);
                error.value = 'Error al cargar el lugar';
                currentRestaurant.value = null;
            }
            finally {
                loading.value = false;
            }
        };
        // Cargar lugar al montar el componente
        onMounted(() => {
            fetchRestaurant();
        });
        // Observar cambios en el ID del lugar
        watch(() => getRestaurantId(), () => {
            fetchRestaurant();
        });
        watch([currentRestaurant, isAuthenticated], () => {
            refreshFavoriteState();
        });
        // Resetear estado de políticas cuando cambia el restaurante
        watch(() => currentRestaurant.value?.id, () => {
            hasLocationPolicies.value = true; // Resetear a true para mostrar durante carga
        });
        // Derivados para nuevas secciones (placeholder)
        const gAmenities = computed(() => {
            return [
                { title: 'Servicios', items: ['Delivery', 'Para llevar', 'Reservas'] },
                { title: 'Ambiente', items: ['Familiar', 'Terraza', 'Música suave'] },
                { title: 'Dietas', items: ['Vegetariano', 'Sin gluten (consultar)'] }
            ];
        });
        const gMenu = computed(() => {
            // SOLO usar items de la base de datos - SIN fallback estático
            if (currentRestaurant.value?.items && Array.isArray(currentRestaurant.value.items)) {
                return currentRestaurant.value.items.map((item) => ({
                    id: item.id,
                    name: item.name,
                    description: item.description,
                    price: item.price,
                    category: getItemTypeLabel(item.type),
                    type: item.type,
                    review: item.review,
                    image_url: item.image_url
                }));
            }
            // Si no hay items en BD, retornar array vacío
            return [];
        });
        const gTransport = computed(() => ['A 10 min del centro en taxi', 'Estación de bus a 3 cuadras']);
        const gPOIs = computed(() => [{ name: 'Plaza principal', distanceKm: 0.8 }, { name: 'Museo', distanceKm: 1.5 }]);
        const gReviews = computed(() => [
            { id: 1, author: 'Carla', rating: 5, date: new Date().toISOString(), text: 'Ambiente agradable.' },
            { id: 2, author: 'Luis', rating: 4, date: new Date().toISOString(), text: 'Buen servicio, volveré.' }
        ]);
        const gReviewSummary = computed(() => {
            const list = gReviews.value;
            const avg = list.length ? list.reduce((a, b) => a + b.rating, 0) / list.length : 0;
            return { average: avg, count: list.length };
        });
        const gPolicies = computed(() => ({ reservations: 'Reservas recomendadas fines de semana', cancellation: 'Hasta 24h antes', hours: '12:00 - 23:00' }));
        const gPayments = computed(() => ['Efectivo', 'Tarjeta de crédito', 'Transferencia']);
        // Computed para determinar si se debe mostrar la sección de políticas
        const shouldShowPolicies = computed(() => {
            if (!currentRestaurant.value)
                return false;
            const allowedTypes = ['restaurant', 'event', 'accommodation'];
            return allowedTypes.includes(currentRestaurant.value.type);
        });
        // Computed para determinar si se debe mostrar la sección de amenities
        const shouldShowAmenities = computed(() => {
            if (!currentRestaurant.value)
                return false;
            const allowedTypes = ['restaurant', 'event', 'accommodation'];
            return allowedTypes.includes(currentRestaurant.value.type);
        });
        // Manejar cuando las políticas se cargan
        const handlePoliciesLoaded = (hasPolicies) => {
            hasLocationPolicies.value = hasPolicies;
        };
        const handleUserLocation = (payload) => {
            console.log('📍 GastronomyPage: handleUserLocation called with:', payload);
            userLatitude.value = payload.latitude;
            userLongitude.value = payload.longitude;
            console.log('📍 GastronomyPage: Updated userLatitude:', userLatitude.value, 'userLongitude:', userLongitude.value);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("main", _hoisted_1, [
                (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[0] || (_cache[0] = [
                        _createElementVNode("div", { class: "spinner" }, null, -1),
                        _createElementVNode("p", null, "Cargando lugar...", -1)
                    ])))
                    : (error.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                            _cache[1] || (_cache[1] = _createElementVNode("i", { class: "fas fa-exclamation-triangle" }, null, -1)),
                            _createElementVNode("p", null, _toDisplayString(error.value), 1),
                            _createElementVNode("button", {
                                onClick: fetchRestaurant,
                                class: "retry-btn"
                            }, "Reintentar")
                        ]))
                        : (currentRestaurant.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createVNode(GastronomyHero, {
                                    featured: {
                                        ...currentRestaurant.value,
                                        regionImage: getMainImage(currentRestaurant.value)
                                    }
                                }, null, 8, ["featured"]),
                                _createVNode(GastronomyHeader, {
                                    title: currentRestaurant.value.name,
                                    subtitle: currentRestaurant.value.description,
                                    location: currentRestaurant.value.location,
                                    rating: currentRestaurant.value.rating,
                                    badge: currentRestaurant.value.cuisine_type,
                                    "favourite-text": favouriteButtonText.value,
                                    "favorite-active": isFavorite.value,
                                    "favorite-loading": favoriteLoading.value,
                                    onToggleFavorite: handleFavoriteToggle
                                }, null, 8, ["title", "subtitle", "location", "rating", "badge", "favourite-text", "favorite-active", "favorite-loading"]),
                                (shouldShowAmenities.value)
                                    ? (_openBlock(), _createBlock(GastronomyAmenitiesSection, {
                                        key: 0,
                                        amenities: gAmenities.value
                                    }, null, 8, ["amenities"]))
                                    : _createCommentVNode("", true),
                                _createVNode(GastronomyDetails, { restaurant: currentRestaurant.value }, null, 8, ["restaurant"]),
                                _createVNode(GastronomyMenuSection, {
                                    items: gMenu.value,
                                    "page-size": 8
                                }, null, 8, ["items"]),
                                _createVNode(GastronomyHowToArriveSection, {
                                    latitude: currentRestaurant.value.latitude,
                                    longitude: currentRestaurant.value.longitude,
                                    address: currentRestaurant.value.location?.address || null,
                                    transport: gTransport.value,
                                    "points-of-interest": gPOIs.value,
                                    onUserLocation: handleUserLocation
                                }, null, 8, ["latitude", "longitude", "address", "transport", "points-of-interest"]),
                                (shouldShowPolicies.value && hasLocationPolicies.value)
                                    ? (_openBlock(), _createBlock(GastronomyPoliciesSection, {
                                        key: 1,
                                        "location-id": currentRestaurant.value.id,
                                        policies: gPolicies.value,
                                        payments: gPayments.value,
                                        onPoliciesLoaded: handlePoliciesLoaded
                                    }, null, 8, ["location-id", "policies", "payments"]))
                                    : _createCommentVNode("", true),
                                _createVNode(GastronomyNearbyCarousel, {
                                    excludeRestaurantId: currentRestaurant.value.id,
                                    currentLocationType: currentRestaurant.value.type || 'restaurant',
                                    currentLocationId: currentRestaurant.value.id,
                                    userLatitude: userLatitude.value,
                                    userLongitude: userLongitude.value
                                }, null, 8, ["excludeRestaurantId", "currentLocationType", "currentLocationId", "userLatitude", "userLongitude"]),
                                _createVNode(GastronomyStickyCTA, {
                                    title: currentRestaurant.value.name,
                                    "phone-or-whatsapp": currentRestaurant.value.whatsapp || null
                                }, null, 8, ["title", "phone-or-whatsapp"])
                            ]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[2] || (_cache[2] = [
                                _createElementVNode("i", { class: "fas fa-map-marker-alt" }, null, -1),
                                _createElementVNode("p", null, "Lugar no encontrado", -1)
                            ])))
            ]));
        };
    }
});
